import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { RiArrowDownSLine } from 'react-icons/ri'
import { GiMining } from 'react-icons/gi'
import CleanEnergyMineralIcon from '../../../icons/CleanEnergyMineralIcon'
import IndustrialMineralsIcon from '../../../icons/IndustrialMineralsIcon'

const cleanEnergyLinks = [
  { label: 'MINERALS_OVERVIEW', route: 'future-minerals?category=1' },
  { label: 'FUTURE_MINERALS_REGION', route: 'future-minerals/region?category=1' },
  { label: 'ARAB_PRODUCTION', route: 'future-minerals/clean-energy/arab-production' },
  { label: 'VALUE_CHAIN', route: 'future-minerals/value-chains?category=1' },
  { label: 'GLOBAL_DEMAND', route: 'future-minerals/clean-energy/global-demand' },
  { label: 'CLEAN_ENERGY_TECHNOLOGIES', route: 'future-minerals/clean-energy/technologies' },
]

const industrialLinks = [
  { label: 'MINERALS_OVERVIEW', route: 'future-minerals?category=2' },
  { label: 'FUTURE_MINERALS_REGION', route: 'future-minerals/region?category=2' },
  { label: 'ARAB_PRODUCTION', route: 'future-minerals/industrial/arab-production' },
  { label: 'VALUE_CHAIN', route: 'future-minerals/value-chains?category=2' },
  { label: 'MAIN_INDUSTRIES', route: 'future-minerals/industrial/industries' },
]

const securityLinks = [
  { label: 'MINERALS_OVERVIEW', route: 'future-minerals?category=4' },
  { label: 'FUTURE_MINERALS_REGION', route: 'future-minerals/region?category=4' },
  { label: 'ARAB_PRODUCTION', route: 'future-minerals/industrial/arab-production' },
  { label: 'VALUE_CHAIN', route: 'future-minerals/value-chains?category=4' },
  { label: 'AGRICULTURAL_APPLICATIONS', route: 'future-minerals/security/securities' },
]

// Chevron that rotates open/closed, shown on every entry that has nested links.
function Chevron({ isOpen, className = '' }) {
  return (
    <RiArrowDownSLine
      size={18}
      className={`shrink-0 transition-transform duration-300 ease-in-out ${isOpen ? 'rotate-180' : ''} ${className}`}
    />
  )
}

// Collapsible wrapper using the grid-rows trick so the accordion animates
// open/closed without measuring pixel heights in JS.
function Collapsible({ isOpen, children }) {
  return (
    <div
      className={`grid overflow-hidden transition-[grid-template-rows] duration-300 ease-in-out ${
        isOpen ? 'grid-rows-[1fr]' : 'grid-rows-[0fr]'
      }`}
    >
      <div className="min-h-0">{children}</div>
    </div>
  )
}

export default function NavbarMobileDropdown({ lng }) {
  const { t } = useTranslation()
  const [isFutureMineralsOpen, setIsFutureMineralsOpen] = useState(false)
  const [isCleanEnergyOpen, setIsCleanEnergyOpen] = useState(false)
  const [isIndustrialOpen, setIsIndustrialOpen] = useState(false)
  const [isSecurityOpen, setIsSecurityOpen] = useState(false)

  const handleToggleFutureMinerals = () => {
    setIsFutureMineralsOpen((prev) => !prev)
    setIsCleanEnergyOpen(false)
    setIsIndustrialOpen(false)
    setIsSecurityOpen(false)
  }

  const handleToggleCleanEnergy = () => {
    setIsCleanEnergyOpen((prev) => !prev)
    setIsIndustrialOpen(false)
    setIsSecurityOpen(false)
  }

  const handleToggleIndustrial = () => {
    setIsIndustrialOpen((prev) => !prev)
    setIsCleanEnergyOpen(false)
    setIsSecurityOpen(false)
  }

  const handleToggleSecurity = () => {
    setIsSecurityOpen((prev) => !prev)
    setIsCleanEnergyOpen(false)
    setIsIndustrialOpen(false)
  }

  return (
    <li>
      <div
        className="hover:bg-light flex cursor-pointer select-none items-center justify-between gap-3 px-6 py-3 transition-colors"
        onClick={handleToggleFutureMinerals}
        role="button"
        aria-expanded={isFutureMineralsOpen}
      >
        <span className="flex items-center gap-3">
          <GiMining size={20} />
          {t('FUTURE_MINERALS')}
        </span>
        <Chevron isOpen={isFutureMineralsOpen} />
      </div>

      <Collapsible isOpen={isFutureMineralsOpen}>
        <div className="flex flex-col gap-3 px-6 pb-4">
          {/* Clean Energy Minerals */}
          <div className="border-cleanEnergy border-2">
            <div
              className="flex cursor-pointer select-none items-center justify-between"
              onClick={handleToggleCleanEnergy}
              role="button"
              aria-expanded={isCleanEnergyOpen}
            >
              <div className="flex items-center">
                <div className="bg-cleanEnergy p-2">
                  <CleanEnergyMineralIcon
                    size={20}
                    color="#fff"
                  />
                </div>
                <div className="text-cleanEnergy whitespace-nowrap px-3 text-sm font-bold">{t('CLEAN_ENERGY_MINERALS')}</div>
              </div>
              <Chevron
                isOpen={isCleanEnergyOpen}
                className="text-cleanEnergy me-3"
              />
            </div>
            <Collapsible isOpen={isCleanEnergyOpen}>
              <ul className="bg-cleanEnergy flex flex-col gap-1 p-3">
                {cleanEnergyLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      className="block py-1 text-sm text-white"
                      to={`/${lng}/${link.route}`}
                    >
                      {t(link.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Collapsible>
          </div>

          {/* Industrial Minerals */}
          <div className="border-industrial border-2">
            <div
              className="flex cursor-pointer select-none items-center justify-between"
              onClick={handleToggleIndustrial}
              role="button"
              aria-expanded={isIndustrialOpen}
            >
              <div className="flex items-center">
                <div className="bg-industrial p-2">
                  <IndustrialMineralsIcon
                    size={20}
                    color="#fff"
                  />
                </div>
                <div className="text-industrial whitespace-nowrap px-3 text-sm font-bold">{t('INDUSTRIAL_MINERALS')}</div>
              </div>
              <Chevron
                isOpen={isIndustrialOpen}
                className="text-industrial me-3"
              />
            </div>
            <Collapsible isOpen={isIndustrialOpen}>
              <ul className="bg-industrial flex flex-col gap-1 p-3">
                {industrialLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      className="block py-1 text-sm text-white"
                      to={`/${lng}/${link.route}`}
                    >
                      {t(link.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Collapsible>
          </div>

          {/* Food Security Minerals */}
          <div className="border-security border-2">
            <div
              className="flex cursor-pointer select-none items-center justify-between"
              onClick={handleToggleSecurity}
              role="button"
              aria-expanded={isSecurityOpen}
            >
              <div className="flex items-center">
                <div className="bg-security p-2">
                  <img
                    src="/assets/icons/security-color-header/security-food-icon.svg"
                    alt=""
                    className="h-5 w-5"
                  />
                </div>
                <div className="text-security whitespace-nowrap px-3 text-sm font-bold">{t('FOOD_SECURITY_MINERALS')}</div>
              </div>
              <Chevron
                isOpen={isSecurityOpen}
                className="text-security me-3"
              />
            </div>
            <Collapsible isOpen={isSecurityOpen}>
              <ul className="bg-security flex flex-col gap-1 p-3">
                {securityLinks.map((link) => (
                  <li key={link.label}>
                    <Link
                      className="block py-1 text-sm text-white"
                      to={`/${lng}/${link.route}`}
                    >
                      {t(link.label)}
                    </Link>
                  </li>
                ))}
              </ul>
            </Collapsible>
          </div>
        </div>
      </Collapsible>
    </li>
  )
}
